import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Award, FileText, BarChart3, Share2,
    Network, Users, BookOpen, Sparkles, ChevronDown,
} from "lucide-react";
import PublicLayout from "./PublicLayout";
import NetworkCanvas from "./NetworkCanvas";
import ScoreDistribution from "./ScoreDistribution";
import { calculateOverallScore, sectionColor } from "../service/Operations";
import {
    fetchTutorialManuscript, fetchTutorialAnalysis, fetchTutorialDeepThinkers,
    fetchTutorialTopSimilarPapers, downloadTutorialReport, trackAdVisit,
} from "../service/TutorialService";
import { properties } from "../properties";

const SCORE_BLURBS = {
    Novelty: "How new the ideas are relative to existing literature",
    Methodology: "How rigorous and reproducible the methods are",
    Impact: "How much the findings could move the field",
    Overall: "The combined signal across all three dimensions",
};

// Illustrative platform distribution for the comparison step — the live
// per-paper percentile endpoint requires an account, so the tutorial shows
// the real sample scores against a representative curve instead.
function buildDemoDistribution(paper) {
    const buckets = ["0-9", "10-19", "20-29", "30-39", "40-49", "50-59", "60-69", "70-79", "80-89", "90-100"];
    const mids = [4.5, 14.5, 24.5, 34.5, 44.5, 54.5, 64.5, 74.5, 84.5, 95];
    const overall = calculateOverallScore(paper);
    const scores = {
        novelty: paper.noveltyScore ?? 0,
        method: paper.methodScore ?? 0,
        impact: paper.impactScore ?? 0,
        overall: overall ?? 0,
    };
    const meta = { novelty: [58, 16], method: [62, 14], impact: [55, 17], overall: [58, 13] };
    const distribution = {};
    const percentiles = {};
    Object.entries(meta).forEach(([key, [mean, std]]) => {
        distribution[key] = {
            mean,
            std,
            histogram: buckets.map((bucket, i) => {
                const z = (mids[i] - mean) / std;
                return { bucket, count: Math.round(40 * Math.exp(-0.5 * z * z)) };
            }),
        };
        // Normal CDF via the Abramowitz–Stegun erf approximation
        const z = (scores[key] - mean) / (std * Math.SQRT2);
        const t = 1 / (1 + 0.3275911 * Math.abs(z));
        const erf = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-z * z);
        const cdf = 0.5 * (1 + Math.sign(z) * erf);
        percentiles[key] = Math.min(99, Math.max(1, Math.round(cdf * 100)));
    });
    return { scores, percentiles, population: 1, distribution };
}

// Owns its loading state so the overlay resets whenever the iframe remounts
// (Back/Next navigation across the network step, or the mobile/desktop swap)
function GraphFrame() {
    const [loading, setLoading] = useState(true);
    return (
        <div className="submissions-card tutorial-graph-frame">
            {loading && (
                <div className="tutorial-graph-loader">
                    <NetworkCanvas />
                    <p className="tutorial-graph-loader-text">Building the network…</p>
                </div>
            )}
            <iframe
                src={`${properties.host}/sample/graph`}
                title="Research network"
                className="tutorial-graph-iframe"
                onLoad={() => setTimeout(() => setLoading(false), 1500)}
            />
        </div>
    );
}

function StepIntro({ icon: Icon, title, children }) {
    return (
        <div className="tutorial-step-intro">
            <div className="tutorial-step-icon"><Icon size={20} /></div>
            <div>
                <h2 className="tutorial-step-title">{title}</h2>
                <p className="tutorial-step-copy">{children}</p>
            </div>
        </div>
    );
}

function AnalysisGroups({ grouped, labelMap }) {
    // First group open so the step never looks empty
    const [open, setOpen] = useState(() => {
        const first = Object.keys(grouped)[0];
        return first ? { [first]: true } : {};
    });
    const toggle = (key) => setOpen(s => ({ ...s, [key]: !s[key] }));
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {Object.entries(grouped).map(([scoreType, items]) => {
                const isOpen = !!open[scoreType];
                return (
                    <div key={scoreType} className="submissions-card">
                        <button className="analysis-group-header" onClick={() => toggle(scoreType)}>
                            <span>{labelMap[scoreType] ?? scoreType} Analysis</span>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span className="analysis-group-count">{items.length} {items.length === 1 ? "item" : "items"}</span>
                                <ChevronDown size={15} className={`analysis-chevron ${isOpen ? "analysis-chevron-open" : ""}`} />
                            </div>
                        </button>
                        {isOpen && (
                            <div className="analysis-items">
                                {items.map((item, i) => (
                                    <div key={item.id ?? i} className="analysis-item">
                                        {item.severity && (
                                            <span className={`analysis-severity severity-${item.severity.toLowerCase()}`}>
                                                {item.severity.charAt(0).toUpperCase() + item.severity.slice(1).toLowerCase()} Priority
                                            </span>
                                        )}
                                        <p className="analysis-issue">{item.issue}</p>
                                        {item.quote && <blockquote className="analysis-quote">{item.quote}</blockquote>}
                                        {item.suggestion && <p className="analysis-suggestion">{item.suggestion}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function Tutorial() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(0);
    const [paper, setPaper] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [deepThinkers, setDeepThinkers] = useState([]);
    const [similarPapers, setSimilarPapers] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(false);
    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 991);
    const topRef = useRef(null);

    // Attribution beacon — once per session, before any data fetch can fail
    useEffect(() => {
        trackAdVisit("/tutorial", searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onResize = () => setSmallScreen(window.innerWidth < 991);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        fetchTutorialManuscript()
            .then(res => { if (res.manuscript) setPaper(res.manuscript); })
            .catch(() => {});
        fetchTutorialAnalysis()
            .then(res => setAnalyses(res.analyses ?? []))
            .catch(() => {});
        fetchTutorialDeepThinkers()
            .then(res => setDeepThinkers((res.deepThinkers ?? []).slice(0, 5)))
            .catch(() => {});
        fetchTutorialTopSimilarPapers()
            .then(res => setSimilarPapers(res.similarPapers ?? []))
            .catch(() => {});
    }, []);

    const demoDistribution = useMemo(
        () => (paper ? buildDemoDistribution(paper) : null),
        [paper]
    );

    const handleExportDownload = async () => {
        setExporting(true);
        try {
            const { blob, disposition } = await downloadTutorialReport();
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "").trim() || "sample-report.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
            setExported(true);
        } catch {
        } finally {
            setExporting(false);
        }
    };

    const scoreCards = paper ? [
        { label: "Novelty", value: paper.noveltyScore },
        { label: "Methodology", value: paper.methodScore },
        { label: "Impact", value: paper.impactScore },
        { label: "Overall", value: calculateOverallScore(paper) },
    ] : [];

    const analysisOrder = ["novelty", "methods", "impact"];
    const analysisLabel = { novelty: "Novelty", methods: "Methodology", impact: "Impact" };
    const analysisGrouped = analysisOrder.reduce((acc, type) => {
        const items = analyses.filter(a => a.scoreType === type);
        if (items.length > 0) acc[type] = items;
        return acc;
    }, {});
    // Fallback when the sample paper has no structured analysis rows: the
    // manuscript itself carries a narrative per dimension.
    const narrativeFallback = paper ? [
        { label: "Novelty", text: paper.novelty },
        { label: "Methodology", text: paper.method },
        { label: "Impact", text: paper.impact },
    ].filter(n => n.text?.trim()) : [];

    const steps = [
        {
            key: "welcome",
            title: "Welcome",
            render: () => (
                <>
                    <StepIntro icon={Sparkles} title="See how DeepThought reads a paper">
                        DeepThought analyzes scientific manuscripts with AI — scoring novelty,
                        methodology, and impact, mapping the surrounding research landscape, and
                        surfacing the researchers closest to your work. This two-minute tour walks
                        through a real paper that DeepThought analyzed. Hit Next to begin.
                    </StepIntro>
                    <div className="submissions-card tutorial-paper-card">
                        <div className="sample-badge">Sample Paper</div>
                        {paper ? (
                            <>
                                <h3 className="tutorial-paper-title">{paper.title || paper.name}</h3>
                                {paper.authors && <p className="upload-page-meta">{paper.authors}</p>}
                            </>
                        ) : (
                            <div className="skeleton" style={{ height: 22, width: "70%", marginTop: 8 }} />
                        )}
                    </div>
                </>
            ),
        },
        {
            key: "scores",
            title: "Scores",
            render: () => (
                <>
                    <StepIntro icon={Award} title="Three scores, minutes after upload">
                        Every paper gets scored 0–100 on novelty, methodology, and impact —
                        grounded in the surrounding literature, not just the text in isolation.
                        These are the real scores for the sample paper.
                    </StepIntro>
                    <div className="submission-scores">
                        {scoreCards.map((s) => (
                            <div key={s.label} className="submission-score-card">
                                <p className="submission-score-label">{s.label}</p>
                                <p className="submission-score-value">{s.value}</p>
                                <p className="tutorial-score-blurb">{SCORE_BLURBS[s.label]}</p>
                            </div>
                        ))}
                    </div>
                </>
            ),
        },
        {
            key: "analysis",
            title: "Analysis",
            render: () => (
                <>
                    <StepIntro icon={FileText} title="Written analysis, not just numbers">
                        Behind each score is a written review: specific issues, the exact passage
                        they refer to, and a concrete suggestion — like a fast, thorough referee
                        report. Expand a section below.
                    </StepIntro>
                    {Object.keys(analysisGrouped).length > 0 ? (
                        <AnalysisGroups grouped={analysisGrouped} labelMap={analysisLabel} />
                    ) : narrativeFallback.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {narrativeFallback.map(n => (
                                <div key={n.label} className="submissions-card" style={{ padding: "16px 18px" }}>
                                    <p className="similar-heading" style={{ marginBottom: 8 }}>{n.label}</p>
                                    <p className="tutorial-narrative">{n.text}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="settings-empty">Analysis is loading…</p>
                    )}
                </>
            ),
        },
        {
            key: "compare",
            title: "Compare",
            render: () => (
                <>
                    <StepIntro icon={BarChart3} title="See where a paper stands">
                        Scores come with context: how the paper compares against everything else
                        scored on the platform, per dimension. The curves below are illustrative —
                        your own dashboard compares against the live platform distribution.
                    </StepIntro>
                    {demoDistribution && (
                        <ScoreDistribution
                            data={demoDistribution}
                            title="How this paper compares"
                            subtitle="Illustrative distribution — sign up to see the live comparison"
                            scoreLabel="Paper score"
                        />
                    )}
                </>
            ),
        },
        {
            key: "network",
            title: "Network",
            render: () => (
                <>
                    <StepIntro icon={Network} title="An interactive map of the field">
                        DeepThought builds a citation-and-similarity network around each paper so
                        you can see the clusters it sits between — and where the white space is.
                        {smallScreen ? " The interactive graph needs a bigger screen, but here's a taste." : " This one is live — drag, zoom, and hover."}
                    </StepIntro>
                    {smallScreen ? (
                        <div className="submissions-card" style={{ overflow: "hidden" }}>
                            <div style={{ height: "30vh", position: "relative", background: "#0f1a14", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <NetworkCanvas />
                                <div className="submission-network-view-hint">Best experienced on a laptop or desktop</div>
                            </div>
                        </div>
                    ) : (
                        <GraphFrame />
                    )}
                </>
            ),
        },
        {
            key: "deepthinkers",
            title: "Deep Thinkers",
            render: () => (
                <>
                    <StepIntro icon={Users} title="The people closest to the work">
                        Deep Thinkers identifies the researchers most connected to a paper —
                        potential reviewers, collaborators, or competitors — with their
                        affiliations and how their work relates.
                    </StepIntro>
                    {deepThinkers.length > 0 ? (
                        <div className="submissions-card" style={{ padding: "6px 18px 14px" }}>
                            <table className="submissions-table" style={{ fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        <th>Researcher</th>
                                        <th>Affiliation</th>
                                        <th style={{ textAlign: "right" }}>Related Papers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deepThinkers.map((dt, i) => (
                                        <tr key={i}>
                                            <td style={{ fontWeight: 500 }}>{dt.name}</td>
                                            <td style={{ color: "var(--ink-500)" }}>{dt.affiliation || "—"}</td>
                                            <td style={{ textAlign: "right" }}>{dt.paperCount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="submissions-card tutorial-feature-fallback">
                            <Users size={26} />
                            <p>
                                For each analyzed paper you get a ranked list of the researchers whose
                                work overlaps most — with affiliations, ORCID profiles, and a summary
                                of how each connects to yours.
                            </p>
                        </div>
                    )}
                </>
            ),
        },
        {
            key: "similar",
            title: "Similar Works",
            render: () => (
                <>
                    <StepIntro icon={BookOpen} title="The most similar published work">
                        DeepThought finds the published papers that overlap most with each
                        manuscript — organized by section, with a similarity score for each. Great
                        for related-work sections and prior-art checks.
                    </StepIntro>
                    {similarPapers.length > 0 ? (
                        <div className="submissions-card">
                            <div className="submission-similar-list">
                                {similarPapers.map((p) => (
                                    <div key={p.id} className="submission-similar-item">
                                        <div className="submission-similar-info">
                                            <p className="submission-similar-title">{p.articleTitle}</p>
                                            {p.section && (
                                                <div style={{ marginTop: 4 }}>
                                                    <span className="similar-works-section-badge" style={sectionColor(p.section)}>{p.section}</span>
                                                </div>
                                            )}
                                            <p className="submission-similar-authors" style={{ marginTop: 4 }}>{p.authors}</p>
                                        </div>
                                        <div className="submission-similar-actions">
                                            <span className="submission-similar-score">{Math.round(p.similarity * 100)}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="settings-empty">Loading similar papers…</p>
                    )}
                </>
            ),
        },
        {
            key: "report",
            title: "Report",
            render: () => (
                <>
                    <StepIntro icon={Share2} title="A report you can hand to anyone">
                        Everything you just saw compiles into a polished PDF report — scores,
                        analysis, similar works — that you can download or share by email straight
                        from the platform. Try it with the sample paper.
                    </StepIntro>
                    <div className="submissions-card tutorial-report-card">
                        <button className="sub-action-btn sub-action-outline" onClick={handleExportDownload} disabled={exporting}>
                            <Share2 size={13} />
                            {exporting ? "Generating…" : "Download the Sample Report"}
                        </button>
                        {exported && <p className="tutorial-report-done">Report downloaded — check your files.</p>}
                    </div>
                </>
            ),
        },
        {
            key: "done",
            title: "Get Started",
            render: () => (
                <div className="tutorial-finish">
                    <Sparkles size={30} />
                    <h2 className="tutorial-step-title" style={{ textAlign: "center" }}>Now try it with your paper</h2>
                    <p className="tutorial-step-copy" style={{ textAlign: "center", maxWidth: 520 }}>
                        Upload a manuscript and get scores, written analysis, a research network,
                        Deep Thinkers, and a shareable report — in minutes.
                    </p>
                    <button className="landing-nav-cta tutorial-finish-cta" onClick={() => navigate("/register")}>
                        Create your free account <ArrowRight size={15} />
                    </button>
                    <button className="tutorial-finish-secondary" onClick={() => navigate("/sample/submission")}>
                        Or keep exploring the sample paper
                    </button>
                </div>
            ),
        },
    ];

    const goTo = (next) => {
        const clamped = Math.max(0, Math.min(steps.length - 1, next));
        setStep(clamped);
        // Funnel signal for the ad platforms' own analytics
        if (typeof window.gtag === "function" && process.env.NODE_ENV === "production") {
            window.gtag("event", "tutorial_step", { step_index: clamped, step_name: steps[clamped].key });
        }
        topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const isLast = step === steps.length - 1;

    return (
        <PublicLayout activePage="">
            <div className="tutorial-shell" ref={topRef}>
                <div className="tutorial-progress-row">
                    <span className="tutorial-progress-label">Step {step + 1} of {steps.length} · {steps[step].title}</span>
                    <button className="tutorial-skip" onClick={() => goTo(steps.length - 1)}>Skip tour</button>
                </div>
                <div className="tutorial-progress-track">
                    <div className="tutorial-progress-fill" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
                </div>

                <div className="tutorial-step-body">
                    {steps[step].render()}
                </div>

                {!isLast && (
                    <div className="tutorial-nav-row">
                        <button
                            className="tutorial-back"
                            onClick={() => goTo(step - 1)}
                            disabled={step === 0}
                        >
                            <ArrowLeft size={14} /> Back
                        </button>
                        <button className="landing-nav-cta" onClick={() => goTo(step + 1)}>
                            Next <ArrowRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </PublicLayout>
    );
}
